import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Button,
  Input,
  Select,
  Switch,
  IconButton,
  Badge,
  FormControl,
  FormLabel,
  Divider,
  useDisclosure,
} from '@chakra-ui/react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Plus, PencilSimple, Trash, ArrowUp, ArrowDown, DotsSixVertical } from 'phosphor-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useKanban,
  IKanbanCampoDefinicao,
  IKanbanResponse,
  CampoTipo,
} from '../../hooks/useKanban';
import DialogModal from '../DialogModal';

interface KanbanFieldManagerProps {
  isOpen: boolean;
  onClose: () => void;
  projetoId: number;
}

const TIPO_LABELS: Record<CampoTipo, string> = {
  texto: 'Texto',
  numero: 'Numero',
  moeda: 'Moeda (R$)',
  data: 'Data',
  select: 'Selecao',
  checkbox: 'Sim/Nao',
};

const TIPO_COLORS: Record<CampoTipo, string> = {
  texto: 'gray',
  numero: 'blue',
  moeda: 'green',
  data: 'purple',
  select: 'orange',
  checkbox: 'teal',
};

const MAX_CAMPOS = 8;

export const KanbanFieldManager: React.FC<KanbanFieldManagerProps> = ({
  isOpen,
  onClose,
  projetoId,
}) => {
  const queryClient = useQueryClient();
  const {
    getCampoDefinicoes,
    createCampoDefinicao,
    updateCampoDefinicao,
    deleteCampoDefinicao,
    reorderCampoDefinicoes,
  } = useKanban();

  const [showForm, setShowForm] = useState(false);
  const [editingCampo, setEditingCampo] = useState<IKanbanCampoDefinicao | null>(null);
  const [formNome, setFormNome] = useState('');
  const [formTipo, setFormTipo] = useState<CampoTipo>('texto');
  const [formOpcoes, setFormOpcoes] = useState('');
  const [formObrigatorio, setFormObrigatorio] = useState(false);
  const [campoToDelete, setCampoToDelete] = useState<number | null>(null);

  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const { data: camposData } = useQuery<IKanbanResponse<IKanbanCampoDefinicao[]>>({
    queryKey: ['kanban-campos', projetoId],
    queryFn: () => getCampoDefinicoes(projetoId),
    enabled: isOpen,
  });

  const camposFromQuery = camposData?.data || [];
  const [localCampos, setLocalCampos] = useState<IKanbanCampoDefinicao[] | null>(null);
  const campos = localCampos || camposFromQuery;

  // Sincronizar localCampos quando query atualiza
  React.useEffect(() => {
    setLocalCampos(null);
  }, [camposFromQuery]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['kanban-campos', projetoId] });

  const createMutation = useMutation({
    mutationFn: (data: {
      nome: string;
      tipo: CampoTipo;
      opcoes?: string[];
      obrigatorio?: boolean;
    }) => createCampoDefinicao(projetoId, data),
    onSuccess: () => {
      invalidate();
      toast.success('Campo criado!');
      resetForm();
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao criar campo');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      campoId,
      data,
    }: {
      campoId: number;
      data: { nome?: string; opcoes?: string[]; obrigatorio?: boolean };
    }) => updateCampoDefinicao(projetoId, campoId, data),
    onSuccess: () => {
      invalidate();
      toast.success('Campo atualizado!');
      resetForm();
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao atualizar campo');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (campoId: number) => deleteCampoDefinicao(projetoId, campoId),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      toast.success('Campo removido!');
    },
    onError: () => toast.error('Erro ao remover campo'),
  });

  const reorderMutation = useMutation({
    mutationFn: (campoIds: number[]) => reorderCampoDefinicoes(projetoId, campoIds),
    onError: (err: any) => {
      setLocalCampos(null); // Rollback para dados da query
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao reordenar');
    },
    onSettled: () => invalidate(),
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingCampo(null);
    setFormNome('');
    setFormTipo('texto');
    setFormOpcoes('');
    setFormObrigatorio(false);
  };

  const handleEdit = (campo: IKanbanCampoDefinicao) => {
    setEditingCampo(campo);
    setFormNome(campo.nome);
    setFormTipo(campo.tipo);
    setFormOpcoes(campo.opcoes?.join(', ') || '');
    setFormObrigatorio(campo.obrigatorio);
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formNome.trim()) {
      toast.error('Nome do campo e obrigatorio');
      return;
    }

    const opcoes =
      formTipo === 'select'
        ? formOpcoes
            .split(',')
            .map(o => o.trim())
            .filter(Boolean)
        : undefined;

    if (formTipo === 'select' && (!opcoes || opcoes.length === 0)) {
      toast.error('Campos de selecao precisam de pelo menos uma opcao');
      return;
    }

    if (editingCampo) {
      updateMutation.mutate({
        campoId: editingCampo.id,
        data: { nome: formNome.trim(), opcoes, obrigatorio: formObrigatorio },
      });
    } else {
      createMutation.mutate({
        nome: formNome.trim(),
        tipo: formTipo,
        opcoes,
        obrigatorio: formObrigatorio,
      });
    }
  };

  const reorderLocal = (fromIndex: number, toIndex: number) => {
    const newCampos = [...campos];
    const [moved] = newCampos.splice(fromIndex, 1);
    newCampos.splice(toIndex, 0, moved);
    setLocalCampos(newCampos);
    reorderMutation.mutate(newCampos.map(c => c.id));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    reorderLocal(index, index - 1);
  };

  const handleMoveDown = (index: number) => {
    if (index >= campos.length - 1) return;
    reorderLocal(index, index + 1);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    reorderLocal(result.source.index, result.destination.index);
  };

  const confirmDelete = () => {
    if (campoToDelete) {
      deleteMutation.mutate(campoToDelete);
      setCampoToDelete(null);
    }
    onDeleteClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader fontSize="md" fontWeight="600" color="gray.800" pb={2}>
            Campos personalizaveis
            <Text fontSize="12px" color="gray.400" fontWeight="400" mt={0.5}>
              Ate {MAX_CAMPOS} campos por projeto
            </Text>
          </ModalHeader>
          <ModalCloseButton borderRadius="full" />

          <ModalBody pb={6}>
            {/* Lista de campos */}
            {campos.length === 0 && !showForm && (
              <Flex direction="column" align="center" py={8} gap={3}>
                <Text fontSize="sm" color="gray.400">
                  Nenhum campo criado ainda
                </Text>
              </Flex>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="campos-list">
                {provided => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {campos.map((campo, index) => (
                      <Draggable key={campo.id} draggableId={`campo-${campo.id}`} index={index}>
                        {(dragProvided, snapshot) => (
                          <Flex
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            alignItems="center"
                            gap={2}
                            py={2}
                            px={2}
                            borderRadius="lg"
                            bg={snapshot.isDragging ? 'blue.50' : 'transparent'}
                            border="1px solid"
                            borderColor={snapshot.isDragging ? 'blue.200' : 'transparent'}
                            boxShadow={snapshot.isDragging ? 'md' : 'none'}
                            mb={1}
                            _hover={{ bg: snapshot.isDragging ? 'blue.50' : 'gray.50' }}
                            transition="background 0.1s"
                          >
                            {/* Drag handle */}
                            <Flex
                              {...dragProvided.dragHandleProps}
                              cursor="grab"
                              color="gray.300"
                              _hover={{ color: 'gray.500' }}
                              p={1}
                              flexShrink={0}
                            >
                              <DotsSixVertical size={16} weight="bold" />
                            </Flex>

                            {/* Setas */}
                            <Flex direction="column" gap={0} flexShrink={0}>
                              <IconButton
                                aria-label="Mover para cima"
                                icon={<ArrowUp size={12} weight="bold" />}
                                size="xs"
                                variant="ghost"
                                isDisabled={index === 0}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleMoveUp(index);
                                }}
                                color="gray.400"
                                _hover={{ color: 'gray.700', bg: 'gray.100' }}
                                minW="20px"
                                h="16px"
                                borderRadius="sm"
                              />
                              <IconButton
                                aria-label="Mover para baixo"
                                icon={<ArrowDown size={12} weight="bold" />}
                                size="xs"
                                variant="ghost"
                                isDisabled={index === campos.length - 1}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleMoveDown(index);
                                }}
                                color="gray.400"
                                _hover={{ color: 'gray.700', bg: 'gray.100' }}
                                minW="20px"
                                h="16px"
                                borderRadius="sm"
                              />
                            </Flex>

                            {/* Conteudo */}
                            <Box flex={1} minW={0}>
                              <Flex alignItems="center" gap={2}>
                                <Text
                                  fontSize="13px"
                                  fontWeight="500"
                                  color="gray.700"
                                  noOfLines={1}
                                >
                                  {campo.nome}
                                </Text>
                                <Badge
                                  colorScheme={TIPO_COLORS[campo.tipo]}
                                  fontSize="10px"
                                  borderRadius="md"
                                  px={1.5}
                                  textTransform="none"
                                  flexShrink={0}
                                >
                                  {TIPO_LABELS[campo.tipo]}
                                </Badge>
                                {campo.obrigatorio && (
                                  <Badge
                                    colorScheme="red"
                                    fontSize="9px"
                                    borderRadius="md"
                                    px={1}
                                    flexShrink={0}
                                  >
                                    Obrigatorio
                                  </Badge>
                                )}
                              </Flex>
                              {campo.tipo === 'select' && campo.opcoes && (
                                <Text fontSize="11px" color="gray.400" mt={0.5} noOfLines={1}>
                                  {campo.opcoes.join(', ')}
                                </Text>
                              )}
                            </Box>

                            {/* Acoes */}
                            <Flex gap={1} flexShrink={0}>
                              <IconButton
                                aria-label="Editar campo"
                                icon={<PencilSimple size={14} />}
                                size="xs"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: 'blue.500', bg: 'blue.50' }}
                                onClick={() => handleEdit(campo)}
                              />
                              <IconButton
                                aria-label="Remover campo"
                                icon={<Trash size={14} />}
                                size="xs"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: 'red.500', bg: 'red.50' }}
                                onClick={() => {
                                  setCampoToDelete(campo.id);
                                  onDeleteOpen();
                                }}
                              />
                            </Flex>
                          </Flex>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>

            {/* Formulário */}
            {showForm && (
              <>
                <Divider my={3} />
                <Box bg="gray.50" p={4} borderRadius="xl">
                  <Text fontSize="13px" fontWeight="600" color="gray.700" mb={3}>
                    {editingCampo ? 'Editar campo' : 'Novo campo'}
                  </Text>

                  <Flex gap={3} mb={3}>
                    <FormControl flex={1}>
                      <FormLabel fontSize="12px" color="gray.500" mb={1}>
                        Nome
                      </FormLabel>
                      <Input
                        size="sm"
                        value={formNome}
                        onChange={e => setFormNome(e.target.value)}
                        placeholder="Ex: Departamento, Valor..."
                        borderRadius="lg"
                        fontSize="13px"
                        maxLength={100}
                      />
                    </FormControl>

                    {!editingCampo && (
                      <FormControl w="160px">
                        <FormLabel fontSize="12px" color="gray.500" mb={1}>
                          Tipo
                        </FormLabel>
                        <Select
                          size="sm"
                          value={formTipo}
                          onChange={e => setFormTipo(e.target.value as CampoTipo)}
                          borderRadius="lg"
                          fontSize="13px"
                        >
                          {Object.entries(TIPO_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Flex>

                  {(formTipo === 'select' || (editingCampo && editingCampo.tipo === 'select')) && (
                    <FormControl mb={3}>
                      <FormLabel fontSize="12px" color="gray.500" mb={1}>
                        Opcoes (separadas por virgula)
                      </FormLabel>
                      <Input
                        size="sm"
                        value={formOpcoes}
                        onChange={e => setFormOpcoes(e.target.value)}
                        placeholder="Opcao 1, Opcao 2, Opcao 3"
                        borderRadius="lg"
                        fontSize="13px"
                      />
                    </FormControl>
                  )}

                  <Flex alignItems="center" justifyContent="space-between">
                    <Flex alignItems="center" gap={2}>
                      <Switch
                        size="sm"
                        isChecked={formObrigatorio}
                        onChange={e => setFormObrigatorio(e.target.checked)}
                        colorScheme="blue"
                      />
                      <Text fontSize="12px" color="gray.500">
                        Obrigatorio
                      </Text>
                    </Flex>

                    <Flex gap={2}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={resetForm}
                        fontSize="13px"
                        borderRadius="lg"
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        bg="gray.900"
                        color="white"
                        onClick={handleSubmit}
                        isLoading={createMutation.isPending || updateMutation.isPending}
                        _hover={{ bg: 'gray.800' }}
                        fontSize="13px"
                        borderRadius="lg"
                        px={4}
                      >
                        {editingCampo ? 'Salvar' : 'Criar'}
                      </Button>
                    </Flex>
                  </Flex>
                </Box>
              </>
            )}

            {/* Botão adicionar */}
            {!showForm && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Plus size={14} weight="bold" />}
                w="100%"
                mt={campos.length > 0 ? 2 : 0}
                color="gray.500"
                fontWeight="500"
                fontSize="13px"
                borderRadius="lg"
                _hover={{ bg: 'gray.50', color: 'gray.700' }}
                isDisabled={campos.length >= MAX_CAMPOS}
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                title={
                  campos.length >= MAX_CAMPOS
                    ? `Limite de ${MAX_CAMPOS} campos atingido`
                    : undefined
                }
              >
                Adicionar campo
                {campos.length >= MAX_CAMPOS && (
                  <Text as="span" ml={1} fontSize="11px" color="gray.400">
                    (limite atingido)
                  </Text>
                )}
              </Button>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <DialogModal
        isOpen={isDeleteOpen}
        status="warning"
        title="Remover campo"
        description="Tem certeza? Os valores deste campo em todos os cards serao removidos permanentemente."
        onConfirm={confirmDelete}
        onCancel={onDeleteClose}
        confirmLabel="Remover"
        cancelLabel="Cancelar"
      />
    </>
  );
};
